import * as React from 'react';
import { __ } from 'react-i18n';
import styled, { keyframes } from 'styled-components';
import { rem } from 'polished';
import { InfoText, InfoType } from '../../General/InfoText';
import {
  FlexCol,
  FlexRow,
  FlexRowCenter,
} from '../../../Defaults/Flexbox/Flexbox';
import {
  Button,
  ButtonLink,
  ButtonPrimaryLink,
  ButtonFourthLink,
} from '../../General/Button';
import { ImitradeSummaryValue as SummaryValue } from './ImitradeSummaryValue';
import { ImitradeFinalValue as FinalValue } from './ImitradeFinalValue';
import { getCartSummaryInfo } from '../../../../utilities/selectors';
import { getSanitasCartSummaryInfo } from '../../selectors';
import { formatPriceToString, prop, round } from '../../../../utilities';
import { Loader } from './ThirdStep';
import { FreeDeliveryBox } from './FreeDeliveryBox';
import { FormInput } from '../../Form/FormInput';
import { IconType, SvgIcon } from '../../General/SvgIcon';
import { TrustPointComponent } from './TrustPointComponent';

interface Props {
  handleStepChange?: (step: number) => void;
  withoutBottomPart?: boolean;
  data: any;
  user: any;
  isFetching: boolean;
  freeDel: number | null;
  addGiftCard?: any;
  removeGiftCard?: any;
  isFirstStep?: boolean;
  handlePointsChange: (points: any) => void;
  pointsAmountRatio?: number;
}

function SecondStep({
  handleStepChange,
  data,
  user,
  withoutBottomPart = false,
  isFetching,
  freeDel,
  addGiftCard,
  removeGiftCard,
  isFirstStep = false,
  handlePointsChange,
  pointsAmountRatio,
}: Props) {
  const {
    totalPrice,
    totalPriceWithoutVat,
    vatTotal,
    currency,
    totalItemsPrice,
  } = getCartSummaryInfo(data);
  const {
    vatGroup20Total,
    vatGroup20TotalDph,
    vatGroup20TotalWithDph,
  } = getSanitasCartSummaryInfo(data);
  const { items, giftcards, consumed_points } = data;
  const hasNullPriceItems = items.some(i => i.sum_price === 0);

  const [currentGiftCard, setCurrentGiftCard] = React.useState(
    giftcards && giftcards.length > 0
      ? prop(giftcards[0], 'card_number', '')
      : '',
  );

  const [showGiftcardItem, setShowGiftcardItem] = React.useState(
    giftcards && giftcards.length > 0,
  );
  // console.log({ showGiftcardItem, currentGiftCard, giftcards });
  const hasActiveGiftCard =
    currentGiftCard && giftcards && giftcards.length > 0;

  const handleGiftCardChange = e => {
    setCurrentGiftCard(e.target.value);
  };

  const handleGiftCard = () => {
    if (hasActiveGiftCard) {
      removeGiftCard(currentGiftCard);
      setCurrentGiftCard('');
      setShowGiftcardItem(false);
    } else {
      if (currentGiftCard.length > 0) {
        setShowGiftcardItem(true);
        addGiftCard(currentGiftCard);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleGiftCard();
    }
  };

  return (
    <>
      <DesktopFields>
        <SummaryValue
          title={__('Cena tovaru bez DPH')}
          value={`${totalPriceWithoutVat} ${currency}`}
        />
        <SummaryValue
          title={__('DPH 20 %')}
          value={`${vatTotal} ${currency}`}
        />
        <SummaryValue
          title={__('Cena tovaru s DPH')}
          value={`${totalPrice} ${currency}`}
        />
        {showGiftcardItem && prop(data, 'total_giftcards_price', 0) > 0 && (
          <SummaryValue
            title={__('Kupónom ste ušetrili')}
            value={`${round(prop(data, 'total_giftcards_price', 0), 2).toFixed(
              2,
            )} ${currency}`}
          />
        )}
        <FreeDeliveryBox value={freeDel} currency={currency} />
        {isFirstStep && (
          <StyledRow>
            <FormInput
              type="text"
              value={currentGiftCard}
              onChange={handleGiftCardChange}
              placeholder="Použiť zľavový kupón"
              disabled={hasActiveGiftCard}
              shrink={1}
              onKeyDown={handleKeyDown}
            />
            <GiftCardButton
              onClick={handleGiftCard}
              hasValue={!!currentGiftCard}
            >
              <TextSpan hasValue={!!currentGiftCard}>{__('Použiť')}</TextSpan>
              <SvgIconWrapper>
                {' '}
                <SvgIcon
                  width={24}
                  height={24}
                  viewBox={'0 0 24 24'}
                  icon={
                    hasActiveGiftCard ? IconType.xSmall : IconType.rightArrow
                  }
                  fill={'#FFFFFF'}
                  alt={__('Giftcard')}
                  title={__('Giftcard')}
                  cursor={'pointer'}
                />
              </SvgIconWrapper>
            </GiftCardButton>
          </StyledRow>
        )}
        {user && prop(user, 'trust_points') > 0 ? (
          <TrustPointComponent
            handlePointsChange={handlePointsChange}
            cartPrice={totalItemsPrice}
            user={user}
            currency={currency}
            consumedPoints={consumed_points}
            pointsAmountRatio={pointsAmountRatio}
          />
        ) : null}

        <FinalValue
          title={__('Celkom k úhrade s DPH')}
          value={`${totalPrice} ${currency}`}
        />
      </DesktopFields>
      <MobileFields>
        <SummaryValue
          title={__('Cena bez DPH')}
          value={`${totalPriceWithoutVat} ${currency}`}
        />
        <SummaryValue
          title={__('DPH 20 %')}
          value={`${vatTotal} ${currency}`}
        />
        <SummaryValue
          title={__('Cena s DPH')}
          value={`${totalPrice} ${currency}`}
        />
        {showGiftcardItem && prop(data, 'total_giftcards_price', 0) > 0 && (
          <SummaryValue
            title={__('Kupónom ste ušetrili')}
            value={`${round(prop(data, 'total_giftcards_price', 0), 2).toFixed(
              2,
            )} ${currency}`}
          />
        )}
        <FreeDeliveryBox value={freeDel} currency={currency} />
        {isFirstStep && (
          <StyledRow>
            <FormInput
              type="text"
              value={currentGiftCard}
              onChange={handleGiftCardChange}
              placeholder="Použiť zľavový kupón"
              disabled={hasActiveGiftCard}
              shrink={1}
              maxWidthPx={900}
              maxWidthMobile={900}
            />
            <GiftCardButton
              onClick={handleGiftCard}
              hasValue={!!currentGiftCard}
            >
              <TextSpan hasValue={!!currentGiftCard}>{__('Použiť')}</TextSpan>
              <SvgIconWrapper>
                {' '}
                <SvgIcon
                  width={24}
                  height={24}
                  viewBox={'0 0 24 24'}
                  icon={
                    hasActiveGiftCard ? IconType.xSmall : IconType.rightArrow
                  }
                  fill={'#FFFFFF'}
                  alt={__('Giftcard')}
                  title={__('Giftcard')}
                  cursor={'pointer'}
                />
              </SvgIconWrapper>
            </GiftCardButton>
          </StyledRow>
        )}
        {user && prop(user, 'trust_points') > 0 ? (
          <TrustPointComponent
            handlePointsChange={handlePointsChange}
            cartPrice={totalItemsPrice}
            user={user}
            currency={currency}
            consumedPoints={consumed_points}
            pointsAmountRatio={pointsAmountRatio}
          />
        ) : null}
        <FinalValue
          title={__('Celkom k úhrade s DPH')}
          value={`${totalPrice} ${currency}`}
        />
      </MobileFields>
      {!withoutBottomPart && (
        <>
          {hasNullPriceItems && (
            <InfoText
              type={InfoType.INFO}
              noIcon={true}
              info={__(
                'Pozor! V košíku sa vyskytujú produkty, ktoré nemajú uvedenú cenu. Ich cena vám bude upresnená.',
              )}
              marginBottom={24}
            />
          )}
          <NextButton
            disabled={isFetching}
            // tslint:disable-next-line:jsx-no-lambda
            onClick={() => (handleStepChange ? handleStepChange(3) : {})}
          >
            {isFetching ? (
              <FlexRowCenter>
                <Loader className="marginLeft" small={true} />
              </FlexRowCenter>
            ) : (
              __('Pokračovať v objednávke')
            )}
          </NextButton>
          <BackButton
            // tslint:disable-next-line:jsx-no-lambda
            onClick={() => (handleStepChange ? handleStepChange(1) : {})}
          >
            {__('Späť')}
          </BackButton>
        </>
      )}
    </>
  );
}

const NextButton = styled(ButtonPrimaryLink)`
  width: 100%;
  max-width: ${rem(328)};
  margin-top: ${rem(32)};

  ${({ theme }) => theme.mediab.s450`
    margin-top: 0;
  `}
`;

const BackButton = styled(ButtonFourthLink)`
  max-width: ${rem(328)};
  margin-top: ${rem(16)};
`;

const DesktopFields = styled(FlexCol)`
  width: 100%;
  max-width: ${rem(328)};

  ${({ theme }) => theme.mediab.l925`
    display: none;
  `}
`;

const MobileFields = styled(FlexCol)`
  display: none;
  margin-bottom: ${rem(16)};
  width: 100%;

  ${({ theme }) => theme.mediab.l925`
    display: flex;
  `}
`;

const StyledRow = styled(FlexRow)`
  margin-top: ${rem(8)};

  &:last-child {
    margin-bottom: 0;
  }

  ${({ theme }) => theme.mediab.l925`
    width: 100%;
    justify-content: center;
  `};
`;

const slideIn = keyframes`
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const GiftCardButton = styled(Button)<{ hasValue?: boolean }>`
  background: ${({ theme }) => theme.colors.primary};
  transition: filter 0.2s ease, width 0.5s ease-in-out;
  margin-left: ${rem(8)};
  padding: 0 ${rem(16)};
  display: flex;
  align-items: center;
  justify-content: ${({ hasValue }) => (hasValue ? 'flex-start' : 'center')};
  width: ${({ hasValue }) => (hasValue ? rem(154) : rem(48))};
  min-width: ${rem(54)};
  position: relative;
  overflow: hidden;

  ${({ theme }) => theme.mediab.s450`
    width: ${({ hasValue }) => (hasValue ? rem(106) : rem(48))};
    padding: 0 ${rem(8)};
  `};
`;

const TextSpan = styled.span<{ hasValue?: boolean }>`
  position: absolute;
  left: ${({ hasValue }) => (hasValue ? rem(16) : '100%')};
  display: ${({ hasValue }) => (hasValue ? 'inline' : 'none')};
  animation: ${({ hasValue }) => (hasValue ? slideIn : 'none')} 0.5s ease-in-out;
  margin-right: ${rem(8)};
  padding-top: ${rem(2)};
`;

const SvgIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: ${rem(16)};
`;

export { SecondStep };
